import { type FC, useEffect, useMemo } from 'react';
import { SidebarCollapseItem, SidebarItem, type TestProps } from '@direct-frontend/components';
import { matchPath } from '@direct-frontend/modules-router';
import { useToggle } from '@direct-frontend/stdlib';
import { useAlwaysShow, useNeverNotify } from '../DashboardLayout/lib/menuItemHooks';
import { type SidebarMenuItem as SidebarMenuItemType } from '../DashboardLayout/lib/types';

type SidebarMenuItemProps = { menuItem: SidebarMenuItemType; locationPathname: string } & TestProps;

const mathRoutes = (routes: string[], locationPathname: string) =>
    routes.some((route) => matchPath(route, locationPathname));

export const SidebarMenuItem: FC<SidebarMenuItemProps> = ({
    menuItem,
    locationPathname,
    'data-testid': dataTestId = `SidebarLink.${menuItem.itemId}`,
}) => {
    const { useShow = useAlwaysShow, useHasNotification = useNeverNotify } = menuItem;
    const isShow = useShow();
    const hasNotification = useHasNotification();

    const isActive = useMemo(() => mathRoutes(menuItem.routes, locationPathname), [menuItem.routes, locationPathname]);

    const routeShouldExpand = useMemo(
        () => Boolean(isActive || menuItem.subMenuItems?.some(({ routes }) => mathRoutes(routes, locationPathname))),
        [isActive, menuItem.subMenuItems, locationPathname]
    );

    const {
        toggle: toggleExpanded,
        toggleOn: expand,
        toggleOff: collapse,
        value: isExpanded,
    } = useToggle(routeShouldExpand);

    useEffect(() => {
        if (routeShouldExpand) {
            expand();
        } else {
            collapse();
        }
    }, [locationPathname, routeShouldExpand, expand, collapse]);

    if (!isShow) {
        return null;
    }

    return (
        <SidebarCollapseItem
            shouldContentGrow={false}
            itemId={menuItem.itemId}
            title={menuItem.title}
            isActive={isActive}
            isExpanded={isExpanded}
            onToggleExpanded={toggleExpanded}
            href={menuItem.href}
            hasNotification={hasNotification}
            data-testid={dataTestId}
        >
            {menuItem.subMenuItems?.map((subMenuItem) => (
                <SidebarMenuSubItem
                    key={subMenuItem.itemId}
                    menuItem={subMenuItem}
                    locationPathname={locationPathname}
                    data-testid={`${dataTestId}.${subMenuItem.itemId}`}
                />
            ))}
        </SidebarCollapseItem>
    );
};

const SidebarMenuSubItem: FC<SidebarMenuItemProps> = ({
    menuItem,
    locationPathname,
    'data-testid': dataTestId = `SidebarLink.${menuItem.itemId}`,
}) => {
    const { useShow = useAlwaysShow, useHasNotification = useNeverNotify } = menuItem;
    const isShow = useShow();
    const hasNotification = useHasNotification();

    if (!isShow) {
        return null;
    }

    const isActive = mathRoutes(menuItem.routes, locationPathname);

    return (
        <SidebarItem
            key={menuItem.itemId}
            itemId={menuItem.itemId}
            title={menuItem.title}
            isTitleBold={false}
            isActive={isActive}
            href={menuItem.href}
            hasNotification={hasNotification}
            data-testid={dataTestId}
        />
    );
};
